import { XMarkIcon } from "@heroicons/react/24/outline";
import type { RunExecutionRefusalReason } from "common";
import { type JSX } from "react";
import { Link } from "react-router-dom";
import { runEnvironmentDeepLink } from "@via/pages/account/profile/run-environments/environment-deep-link.ts";
import {
  LAUNCH_REFUSAL_CONFIRMATION_ID,
  LAUNCH_REFUSAL_PICKER_BUTTON_ID,
  type LaunchRefusalPanelView,
} from "./use-launch-refusal";

/**
 * A launch refusal, rendered inline above Launch (3G-3a spec 5.2, D3). A pure view of state the run page owns
 * (`useLaunchRefusal`): it holds nothing and judges nothing. "refused" and "switched" are two renders of one
 * component, so the confirmation of a switch lives where the refusal was, on every tab, rather than only as a
 * selector on a tab the user is not looking at (D9).
 */

/**
 * One title per reason. A `Record` over the union, so a reason added to `common` with no title here fails to
 * compile. An unknown reason at runtime (a newer backend) falls back to the generic title (D6).
 */
export const LAUNCH_REFUSAL_TITLES: Record<RunExecutionRefusalReason, string> =
  {
    agent_runs_disabled: "Agent runs are not enabled on this server",
    agent_not_attached: "No agent is attached to this run environment",
    agent_offline: "The agent for this run environment is offline",
    agent_upgrade_required:
      "The agent for this run environment needs an upgrade",
    launch_agent_gone: "The agent this run launched on no longer exists",
    agent_termination_unconfirmed:
      "The agent could not confirm that the job stopped",
    agent_control_pool_busy: "The agent is busy, try again in a moment",
    termination_pending: "The run's job is still stopping",
    ssh_only_operation: "This operation needs an SSH run environment",
    organization_connection_pending:
      "The organization's run environment connection is still pending",
    organization_connection_incomplete:
      "The organization's run environment connection is incomplete",
    aws_source_identity_chained:
      "The server's AWS identity is already an assumed role",
    aws_source_identity_unreadable:
      "The server's AWS identity could not be read",
    aws_session_token_unsupported:
      "This Nextflow version cannot use temporary AWS credentials",
    attempt_connection_unpinned: "This attempt has no recorded connection",
    executor_not_supported:
      "This run environment's executor cannot launch runs",
    head_node_binding_mismatch:
      "The run environment's head node does not match its record",
    writable_data_source_on_agent:
      "Agent run environments cannot read from a writable data source",
    aws_batch_request_exceeds_compute_environment:
      "This run asks AWS Batch for more than its compute environment can place",
    aws_ecr_image_outside_account:
      "This run's container image is in another account's private Amazon ECR registry",
    remote_storage_refused:
      "A remote server this run reads from cannot be used",
  };

export const GENERIC_LAUNCH_REFUSAL_TITLE = "Launch refused";

export const launchRefusalTitle = (reason: string): string =>
  Object.prototype.hasOwnProperty.call(LAUNCH_REFUSAL_TITLES, reason)
    ? LAUNCH_REFUSAL_TITLES[reason as RunExecutionRefusalReason]
    : GENERIC_LAUNCH_REFUSAL_TITLE;

export type LaunchRefusalPanelProps = {
  /** null hides the panel. */
  view: LaunchRefusalPanelView;
  /** The run's available environments filtered to transport "ssh" by the page, so transport knowledge stays in one place. */
  sshEnvironments: { id: number; name: string }[];
  onSwitchEnvironment: (id: number) => void;
  onOpenEnvironmentPicker: () => void;
  onDismiss: () => void;
};

const BUTTON_CLASS =
  "w-fit rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600";

const DismissButton = ({
  onDismiss,
}: {
  onDismiss: () => void;
}): JSX.Element => (
  <button
    type="button"
    onClick={onDismiss}
    aria-label="Dismiss"
    className="rounded-md p-1 hover:bg-black/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
    <XMarkIcon className="h-5 w-5" aria-hidden="true" />
  </button>
);

export default function LaunchRefusalPanel({
  view,
  sshEnvironments,
  onSwitchEnvironment,
  onOpenEnvironmentPicker,
  onDismiss,
}: LaunchRefusalPanelProps): JSX.Element | null {
  if (view === null) return null;

  if (view.kind === "switched") {
    return (
      <div
        role="status"
        data-testid="launch-refusal-panel"
        className="mb-3 flex items-start justify-between gap-4 rounded-md border-l-4 border-green-400 bg-green-50 px-4 py-3 text-sm text-green-800">
        <p
          id={LAUNCH_REFUSAL_CONFIRMATION_ID}
          tabIndex={-1}
          className="focus:outline-none">
          Switched to &quot;{view.environmentName}&quot;. Launch when ready.
        </p>
        <DismissButton onDismiss={onDismiss} />
      </div>
    );
  }

  const { refusal } = view;
  const [onlySsh] = sshEnvironments;

  return (
    <div
      role="alert"
      data-testid="launch-refusal-panel"
      className="mb-3 flex items-start justify-between gap-4 rounded-md border-l-4 border-amber-400 bg-amber-50 px-4 py-3 text-sm text-amber-900">
      <div className="flex flex-col gap-2">
        <p className="font-semibold">{launchRefusalTitle(refusal.reason)}</p>
        <p>{refusal.remedy}</p>
        {refusal.reason === "agent_not_attached" && (
          <p>
            {/* agent-4-2 (spec A4). The environment the launch submitted, opened at its Agent card; without one,
                the page itself, as before. */}
            <Link
              to={runEnvironmentDeepLink(view.runEnvironmentId)}
              className="underline">
              {typeof view.runEnvironmentId === "number"
                ? "Open this run environment's Agent card"
                : "Attach an agent on your run environments page"}
            </Link>
          </p>
        )}
        {refusal.fallback && (
          <div className="flex flex-col gap-2">
            {refusal.fallback.dataSources.length > 0 && (
              <>
                <p>
                  The owner of a data source can make it read-only on the data
                  connections page:
                </p>
                <ul className="list-disc pl-5">
                  {refusal.fallback.dataSources.map(({ id, name }) => (
                    <li key={id}>
                      <Link to="/data/create-data-source" className="underline">
                        {name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {sshEnvironments.length === 1 && onlySsh && (
              <button
                type="button"
                className={BUTTON_CLASS}
                onClick={() => onSwitchEnvironment(onlySsh.id)}>
                Switch to SSH environment &quot;{onlySsh.name}&quot;
              </button>
            )}
            {sshEnvironments.length > 1 && (
              <button
                type="button"
                id={LAUNCH_REFUSAL_PICKER_BUTTON_ID}
                className={BUTTON_CLASS}
                onClick={onOpenEnvironmentPicker}>
                Choose an SSH environment
              </button>
            )}
            {sshEnvironments.length === 0 && (
              <p>
                No SSH run environment is available to you. An administrator can
                add one.
              </p>
            )}
          </div>
        )}
      </div>
      <DismissButton onDismiss={onDismiss} />
    </div>
  );
}
